import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkDeleteCalendars } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Calendar } from '../../../../../types';
import React from 'react';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface CalendarDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  isAllUsers: boolean;
  selectedCalendars: Calendar[];
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function CalendarDeleteDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  isAllUsers,
  selectedCalendars,
  setSelectedRows,
}: CalendarDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const moreThanOneSelection = selectedCalendars.length > 1;
  const moreThanOneSecondary =
    selectedCalendars.filter((calendar) => !calendar.primary).length > 1;
  const primarySelected = selectedCalendars.some(
    (calendar) => calendar.primary === true
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      bulkDeleteCalendars(userEmail, selectedCalendars, changeLogMessage),
    onSuccess: () => {
      selectedCalendars.forEach((calendar) => {
        if (calendar.primary) {
          queryClient.invalidateQueries({
            queryKey: [UserKeys.CalendarEvents, calendar.id],
          });
        }
      });
      // Custom snackbar message logic
      const snackbarMessage =
        selectedCalendars.length > 5
          ? 'Bulk calendar deletion has been scheduled in the background. You will be notified when it completes.'
          : getDeleteSnackbarMessage({
              moreThanOneSelection,
              moreThanOneSecondary,
              primarySelected,
            });
      setSnackbar({
        open: true,
        severity: 'success',
        message: snackbarMessage,
      });

      queryClient.setQueriesData(
        { queryKey: [UserKeys.Calendars, userEmail] },
        (oldData: Calendar[] | undefined) => {
          if (oldData) {
            return oldData.filter((calendar) => {
              const isSelected = selectedCalendars.some(
                (selected) => selected.id === calendar.id
              );
              return !(isSelected && !calendar.primary);
            });
          }
          return [];
        }
      );

      setChangeLogMessage('');
      setSelectedRows([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          `calendar${moreThanOneSelection ? 's' : ''}`
        ),
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.delete_calendar
          : analytics.user_management.user_details.calendars.delete_calendar,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, CalendarRoles.DeleteCalendars.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  function getDeleteSnackbarMessage({
    moreThanOneSelection,
    moreThanOneSecondary,
    primarySelected,
  }: {
    moreThanOneSelection: boolean;
    moreThanOneSecondary: boolean;
    primarySelected: boolean;
  }) {
    if (!moreThanOneSelection && primarySelected) {
      return Messages.Success.Wiped('Calendar events');
    }
    if (!moreThanOneSelection && !primarySelected) {
      return Messages.Success.Deleted('Calendar');
    }
    if (moreThanOneSelection && primarySelected && moreThanOneSecondary) {
      return 'Selected secondary calendars deleted and primary calendar events wiped successfully.';
    }
    if (moreThanOneSelection && primarySelected && !moreThanOneSecondary) {
      return 'Selected secondary calendar deleted and primary calendar events wiped successfully.';
    }
    return Messages.Success.Deleted('Calendars');
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        !moreThanOneSelection && primarySelected
          ? 'Clear Calendar'
          : `Delete Calendar${moreThanOneSelection ? 's' : ''}`
      }
      dialogContent={
        <Box>
          <Typography sx={{ mt: 1 }}>
            {moreThanOneSelection ? (
              <>
                Do you wish to delete the selected{' '}
                <strong>{selectedCalendars.length}</strong> calendars from{' '}
                <strong>{userEmail}</strong>'s calendars?{' '}
                {primarySelected &&
                  `Primary calendars can't be deleted, they will have their events removed.`}
              </>
            ) : (
              <>
                {primarySelected ? (
                  <>
                    Do you wish to clear the calendar{' '}
                    <strong>{selectedCalendars[0]?.summary}</strong> from{' '}
                    <strong>{userEmail}</strong>'s calendars? This will remove
                    all of its events.
                  </>
                ) : (
                  <>
                    Do you wish to delete the calendar{' '}
                    <strong>{selectedCalendars[0]?.summary}</strong> from{' '}
                    <strong>{userEmail}</strong>'s calendars?
                  </>
                )}
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title:
          !moreThanOneSelection && primarySelected
            ? 'Delete Events'
            : `Delete Calendar${moreThanOneSelection ? 's' : ''}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
